<template>
  <div id="projects-inbox" class="content-with-spacing">
    <div class="heading-container">
      <h1><i class="fas fa-briefcase" aria-hidden="true"></i>
        Projects Inbox
      </h1>
    </div>
    <div class="panel panel-default">
      <div class="panel-body">
        <div class="button-container">
          <button class="btn btn-primary projects-inbox-btn" @click="getProjects"><i class="fas fa-sync" aria-hidden="true"></i> Reload Table</button>
        </div>
        <k-table class="projects-inbox-table"
          v-if="projectsDataReady"
          :headers="kableHeaders"
          :defaultHeaders="visibleHeaders"
          :rows="kableRows"
          :hideable="false"
          :panel="false"
          :max="10"
          @clicked="handleTableAction"
        ></k-table>
      </div>
    </div>
    <project-general-feedback
      :show-modal="showFeedbackModal"
      :selected-project-data="selectedProjectData"
      @close="clearModalData"
    />
  </div>
</template>

<style>
.count-bubble.count-bubble-pending {
  background-color: var(--kate-warning) !important;
  color: var(--kate-type-dark) !important;
}

.count-bubble.count-bubble-complete {
  background-color: var(--kate-success) !important;
  color: var(--kate-type-dark) !important;
}

.projects-inbox-table .k-table-action.clickable:hover i {
  color: var(--kate-type-link-hover);
}
</style>

<script>
import KTable from '@base-components/tables/display/k-table.vue';
import { formatId } from '@utils/format-user-id';
import PageReadyMixin from '@mixins/page-ready-mixin';
import useGlobalStore from '@stores/global';
import ProjectGeneralFeedback from './components/project-general-feedback.vue';

export default {
  mixins: [PageReadyMixin],

  components: {
    KTable,
    ProjectGeneralFeedback,
  },

  data() {
    return {
      store: useGlobalStore(),
      projects: [],
      projectsDataReady: false,
      showFeedbackModal: false,
      selectedProjectData: {
        projectId: undefined,
        programmeId: undefined,
        studentId: undefined,
      },
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getProjects();
  },

  computed: {
    ready() {
      return this.projectsDataReady;
    },

    visibleHeaders() {
      return ['student_id', 'student_name', 'name', 'programme_name', 'description', 'items_to_review', 'status', 'scoping_status', 'view_project', 'general_feedback'];
    },

    coachUserId() {
      return this.store.userId;
    },

    kableHeaders() {
      return {
        raw_student_id: {
          name: 'Student ID',
          type: 'string',
          filterable: true,
          sortable: true,
        },
        student_id: {
          name: 'Student ID',
          type: 'string',
          filterable: true,
          sortable: true,
        },
        student_name: {
          name: 'Student',
          type: 'url',
          filterable: true,
          sortable: true,
        },
        name: {
          name: 'Project Name',
          type: 'string',
          filterable: true,
          sortable: true,
        },
        programme_id: {
          name: 'Programme ID',
          type: 'string',
          filterable: true,
          sortable: true,
        },
        programme_name: {
          name: 'Programme',
          type: 'string',
          hasDropdown: true,
        },
        description: {
          name: 'Description',
          type: 'string',
        },
        items_to_review: {
          name: 'Items to Review',
          type: 'function',
          sortable: true,
          displayAs: row => {
            const count = row.items_to_review;
            if (count) {
              return `<span class="count-bubble count-bubble-pending">${count} items to review</span>`;
            }
            return '<span class="count-bubble count-bubble-complete">All reviewed</span>';
          },
        },
        status: {
          name: 'Status',
          type: 'pill',
          filterable: true,
          sortable: true,
          displayAs: status => (status === 'scoping_proposed' ? 'scope_proposed' : status),
          options: {
            type: status => ({
              completed: 'success',
              in_progress: 'primary',
              draft: 'neutral',
              scope_proposed: 'proposed',
            }[status] || 'neutral'),
          },
        },
        scoping_status: {
          name: 'Scoping',
          type: 'string',
        },
        view_project: {
          name: 'View Project',
          type: 'action',
          options: {
            callback: this.handleTableAction,
          },
        },
        general_feedback: {
          name: 'General Feedback',
          type: 'action',
          options: {
            callback: this.handleTableAction,
          },
        },
      };
    },
    kableRows() {
      return this.projects.map((project, index) => ({
        id: project.id,
        raw_student_id: project.student_id,
        student_id: formatId(project.student_id),
        student_name: {
          text: project.student_name,
          path: {
            name: 'dashboard_students',
            params: {
              studentId: project.student_id,
            },
          },
        },
        name: project.name,
        programme_id: project.programme_id,
        programme_name: project.programme_name,
        items_to_review: project.items_to_review,
        description: project.description && project.description.length > 50
          ? `${project.description.substring(0, 50)}...`
          : project.description || '',
        status: project.status,
        scoping_status: project.scoping_completed ? 'Completed' : 'Pending',
        view_project: {
          key: index,
          text: '<i class="fas fa-external-link-alt" aria-hidden="true"></i>',
        },
        general_feedback: {
          key: index,
          text: '<i class="fas fa-edit" aria-hidden="true"></i>',
        },
      }));
    },
  },

  methods: {
    getProjects() {
      this.projectsDataReady = false;
      this.$logger.info('Fetching projects', { coachUserId: this.coachUserId });
      this.$http.get('/api/curriculum/admin/programmes/projects/')
        .then(response => {
          this.$logger.info('Successfully fetched projects', { coachUserId: this.coachUserId });
          this.projects = response.data;
        })
        .catch(error => {
          this.$logger.error('Error fetching projects', { coachUserId: this.coachUserId }, error);
          this.$ktoast.showError(error, true);
        }).then(() => {
          this.projectsDataReady = true;
        });
    },

    handleTableAction(key, index, row, column) {
      if (column === 'view_project') {
        this.viewProject(row);
      } else if (column === 'general_feedback') {
        this.selectedProjectId = row.id;
        this.selectedProjectData.projectId = row.id;
        this.selectedProjectData.programmeId = row.programme_id;
        this.selectedProjectData.studentId = row.raw_student_id;
        this.showFeedbackModal = true;
      }
    },

    clearModalData() {
      this.selectedProjectData = {};
      this.showFeedbackModal = false;
    },

    viewProject(row) {
      this.$router.push({
        name: 'project_review_page',
        params: {
          projectId: row.id,
        },
      });
    },
  },
};
</script>
